import Spinner from '@/components/atoms/Spinner';
import Text from '@/components/atoms/Text';
import {
  ChatIcon,
  EmailIcon,
  MessengerIcon,
  SMSIcon,
  WhatsAppIcon,
} from '@/components/molecules/FilterTabs/icons';
import { Tab, TabPanel } from '@/components/molecules/Tab';
import { CONVERSATION_SORT, CONVERSATION_STATUS } from '@/constants';
import { useChatConversation } from '@/hooks/useChatConversation';
import routeNames from '@/routes/routeNames';
import { AuthStore } from '@/state/AuthenticationStore';
import { ConStore } from '@/state/ConversationStore';
import { User } from '@/types/user.types';
import { replaceRouteParam } from '@/utils/routes';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import ChatArea from '../../organisms/ChatArea';
import ConversationList from '../../organisms/ConversationList';
import ConversationProfilePanel from '../../organisms/ConversationProfilePanel';
import NoConversations from '../NoConversations';
import NoConversationSelected from '../NoConversationSelected';
import { RiInboxArchiveFill } from 'react-icons/ri';
import { useContact } from '@/hooks/useContact';
import { contactStore } from '@/state/ContactStore';
import { useParams } from 'react-router-dom';
import { CONVERSATION_LIST_MODE } from '@/hooks/useChatConversation/types';

export interface MainChatUiProps {
  title: 'Chat' | 'Email' | 'SMS' | 'Messenger' | 'Whatsapp';
  conversationChannel?: 'CHAT' | 'EMAIL' | 'SMS' | 'MESSENGER' | 'WHATSAPP';
  showLeftPanel?: boolean;
  userData?: User | null;
}

const titles: MainChatUiProps['title'][] = [
  'Chat',
  'Email',
  'SMS',
  'Messenger',
  'Whatsapp',
];
const conversationChannel: MainChatUiProps['conversationChannel'][] = [
  'CHAT',
  'EMAIL',
  'SMS',
  'MESSENGER',
  'WHATSAPP',
];

const sortData = [{ name: 'Latest' }, { name: 'Oldest' }];
const filterData = [
  {
    name: 'Open',
    status: CONVERSATION_STATUS.OPEN,
    icon: (
      <svg
        className='w-5 h-5  pointer-events-none group-hover:text-primary-light'
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
        strokeWidth={1.5}
        stroke='currentColor'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z'
        />
      </svg>
    ),
    count: 2,
  },
  {
    name: 'Archived',
    status: CONVERSATION_STATUS.CLOSED,
    icon: (
      <RiInboxArchiveFill className='w-5 h-5  pointer-events-none group-hover:text-primary-light' />
    ),
    count: 4,
  },
];

const MainChatUi: FC<MainChatUiProps> = props => {
  const [title, setTitle] = useState<MainChatUiProps['title']>(
    props.title || 'Chat',
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [channelIndex, setChannelIndex] = useState(0);
  const [, setPrevIndex] = useState(0);
  const [, setNextIndex] = useState(2);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [chatCount, setChatCount] = useState(0);
  const [smsCount, setSmsCount] = useState(0);
  const [sortedBy, setSortedBy] = useState(sortData[0]);
  const [filterState, setFilterState] = useState(filterData[0]);
  const { getConversations } = useChatConversation();
  const { getContacts } = useContact();
  const { id } = useParams();

  const navigate = (index: number) => {
    setPrevIndex(currentIndex - 1);
    setNextIndex(currentIndex + 1);
    setCurrentIndex(index);
  };

  const goBack = () => {
    setCurrentIndex(currentIndex - 1);
  };
  const next = () => {
    setCurrentIndex(currentIndex + 1);
  };
  const navigation = {
    navigate,
    goBack,
    next,
  };
  const handleTabChange = (index: number) => {
    const tabTitle = titles[index];
    setTitle(tabTitle);
    setChannelIndex(index);
  };

  useEffect(() => {
    getConversations({
      page: 1,
      status: filterState.status,
      sort: ConStore.sorted_by === CONVERSATION_SORT.LATEST ? -1 : 1,
      channel: conversationChannel[channelIndex],
      ...(props.userData && { contactId: props.userData._id }),
      mode: id
        ? CONVERSATION_LIST_MODE.CONTACT_VIEW
        : CONVERSATION_LIST_MODE.GENERAL_VIEW,
    });
    getContacts(`?contactType=all`);
  }, [
    sortedBy,
    filterState,
    ConStore.search_conversations,
    conversationChannel[channelIndex],
  ]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    ConStore.unread_channel_conversations_counts.map(convo => {
      if (convo.name == 'CHAT') {
        setChatCount(convo.count);
      } else if (convo.name == 'SMS') {
        setSmsCount(convo.count);
      }
    });
  }, [
    ConStore.unread_channel_conversations_counts,
    conversationChannel[channelIndex],
  ]);

  useEffect(() => {
    ConStore.set_selected_conversation_id(null);
    ConStore.set_selected_conversation(null);
  }, [props.conversationChannel, conversationChannel[channelIndex]]);

  const routes = [
    {
      path: 'Conversations',
      element: (
        <div>
          <Text size='lg' className='text-black px-4 py-2'>
            Inbox
          </Text>
          <div className='w-full overflow-x-auto'>
            <Tab onTabChange={handleTabChange}>
              <TabPanel
                icon={<ChatIcon />}
                isIcon={true}
                title='Chat'
                conversationChannel='CHAT'
                isRibbon={true}
                count={chatCount}
              ></TabPanel>
              <TabPanel
                icon={<EmailIcon />}
                isIcon={true}
                title='Email'
                conversationChannel='EMAIL'
              ></TabPanel>
              <TabPanel
                icon={<SMSIcon />}
                isIcon={true}
                title='SMS'
                conversationChannel='SMS'
                isRibbon={true}
                count={smsCount}
              ></TabPanel>
              <TabPanel
                icon={<MessengerIcon />}
                isIcon={true}
                title='Messenger'
                conversationChannel='MESSENGER'
              ></TabPanel>
              <TabPanel
                icon={<WhatsAppIcon />}
                isIcon={true}
                title='Whatsapp'
                conversationChannel='WHATSAPP'
              ></TabPanel>
            </Tab>
          </div>
          <div className='border border-x-0 border-t-0 border-border mt-3'></div>
          <ConversationList
            {...props}
            navigation={navigation}
            sortedBy={sortedBy}
            filterState={filterState}
            setSortedBy={setSortedBy}
            setFilterState={setFilterState}
            filterData={filterData}
            sortData={sortData}
            fullWidth
            title={title}
          />
        </div>
      ),
    },
    {
      path: 'chat',
      element: (
        <div>
          {ConStore.selected_conversation_id !== null ? (
            <div>
              {ConStore.fetchingConversations ||
              ConStore.fetching_closed_conversations ? (
                <div className='flex justify-center items-center h-full w-full'>
                  <Spinner size={40} color='#033EB5' />
                </div>
              ) : (
                <ChatArea navigation={navigation} />
              )}
            </div>
          ) : (
            <NoConversations
              channel={
                props.title === 'Chat'
                  ? 'Configure Your Chat.'
                  : 'Setup SMS Integration.'
              }
              path={replaceRouteParam(
                props.title === 'Chat'
                  ? routeNames.dashboard.configurations
                  : routeNames.dashboard.integration,
                'workspaceId',
                AuthStore?.user_workspace_info.active_workspace.workspace._id,
              )}
            />
          )}
        </div>
      ),
    },

    {
      path: 'details',
      element: <ConversationProfilePanel navigation={navigation} {...props} />,
    },
  ];

  return (
    <>
      {windowWidth > 450 ? (
        <motion.div
          layout
          className='hidden z-10 md:flex flex-row w-full h-full max-h-[94vh] overflow-hidden  shadow-xl border border-gray-20 bg-[#edeff2]'
        >
          <ConversationList
            {...{
              conversationChannel: conversationChannel[channelIndex],
              ...props,
            }}
            sortedBy={sortedBy}
            filterState={filterState}
            setSortedBy={setSortedBy}
            setFilterState={setFilterState}
            filterData={filterData}
            sortData={sortData}
          />
          {ConStore.fetchingConversations ? (
            <div className='flex justify-center items-center h-full w-full'>
              <Spinner size={40} color='#033EB5' />
            </div>
          ) : ConStore.conversations.length > 0 ? (
            ConStore.selected_conversation_id ? (
              <>
                <ChatArea />
                {props.showLeftPanel && (
                  <ConversationProfilePanel
                    navigation={navigation}
                    {...props}
                    {...{
                      conversationChannel: conversationChannel[channelIndex],
                    }}
                  />
                )}
              </>
            ) : (
              <NoConversationSelected />
            )
          ) : (
            <NoConversations
              channel={
                props.title === 'Chat'
                  ? 'Configure Your Chat.'
                  : 'Setup SMS Integration.'
              }
              path={replaceRouteParam(
                props.title === 'Chat'
                  ? routeNames.dashboard.configurations
                  : routeNames.dashboard.integration,
                'workspaceId',
                AuthStore?.user_workspace_info.active_workspace.workspace._id,
              )}
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          layout
          className='md:hidden z-10 w-full h-full  bg-[#ffffff]'
        >
          {routes[currentIndex].element}
        </motion.div>
      )}
    </>
  );
};
export default observer(MainChatUi);
